import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import GlobalView from './GlobalView';
import ServerOrders from './ServerOrders';
import ServerSchedule from './ServerSchedule';
import MyPaiement from '../admin/MyPaiement';
import TakeOrder from './TakeOrder';
import ViewMenuServer from './ViewMenuServer';
import './serverinterface.css';
import { 
  FaSignOutAlt, 
  FaClipboardList, 
  FaHome, 
  FaEye, 
  FaCreditCard, 
  FaCalendarAlt, 
  FaUtensils,
  FaTimes,
  FaUser,
  FaCheck,
  FaTimes as FaCancel
} from 'react-icons/fa';


const ServerInterface = () => {
  const [user, setUser] = useState(null);
  const [serverFirstName, setServerFirstName] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const menuId = "ZifkqWN99oRgRNM0Nisz";
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);

  useEffect(() => {
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('paiement.status', '==', 'en_attente'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingPaymentsCount(snapshot.size); // nombre de paiements en attente
    });

    return () => unsubscribe(); // nettoyage
  }, []);

  useEffect(() => {
    const fetchUserRole = async (currentUser) => {
      try {
        const userRef = doc(db, 'identifications', currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.role !== 'server') {
            setIsAuthorized(false);
            setTimeout(() => navigate('/login'), 3000);
          } else {
            setServerFirstName(userData.firstName);
          }
        } else {
          console.error('Utilisateur non trouvé.');
          setIsAuthorized(false);
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du rôle :', error);
        setIsAuthorized(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchUserRole(currentUser);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const handleMenuClick = (section) => {
    setActiveSection(section);
    setIsMenuOpen(false);
  };

  if (!isAuthorized) {
    return (
      <div className="server-interface-unauthorized-container">
        <div className="server-interface-unauthorized-popup">
          <div className="server-interface-unauthorized-icon">
            <FaTimes />
          </div>
          <h2 className="server-interface-unauthorized-title">Accès Refusé</h2>
          <p className="server-interface-unauthorized-message">Vous n'êtes pas autorisé à accéder à cette page.</p>
          <p className="server-interface-unauthorized-redirect">Redirection en cours...</p>
        </div>
      </div>
    );
  };

  return (
    <div className="server-interface-container">
      {/* Header moderne */}
      <header className="server-interface-header">
        <div className="server-interface-header-content">
          {/* Logo et titre */}
          <div className="server-interface-brand">
            <div className="server-interface-brand-icon">
              <FaUser />
            </div>
            <div className="server-interface-brand-text">
              <h1 className="server-interface-title">Interface Serveur</h1>
              {user && serverFirstName && (
                <p className="server-interface-subtitle">Bienvenue, {serverFirstName}</p>
              )}
            </div>
          </div>

          {/* Navigation desktop */}
          <nav className="server-interface-desktop-nav">
            <div className="server-interface-nav-items">
              <button 
                onClick={() => handleMenuClick('home')} 
                className={`server-interface-nav-item ${activeSection === 'home' ? 'server-interface-nav-item-active' : ''}`}
              >
                <FaHome className="server-interface-nav-icon" />
                <span className="server-interface-nav-text">Accueil</span>
              </button>
              
              <button 
                onClick={() => handleMenuClick('GlobalView')} 
                className={`server-interface-nav-item ${activeSection === 'GlobalView' ? 'server-interface-nav-item-active' : ''}`}
              >
                <FaEye className="server-interface-nav-icon" />
                <span className="server-interface-nav-text">Vue Globale</span>
              </button>
              
              <button 
                onClick={() => handleMenuClick('MyPaiement')} 
                className={`server-interface-nav-item server-interface-nav-item-payments ${activeSection === 'MyPaiement' ? 'server-interface-nav-item-active' : ''}`}
              >
                <FaCreditCard className="server-interface-nav-icon" />
                <span className="server-interface-nav-text">Gérer les paiements</span>
                {pendingPaymentsCount > 0 && (
                  <span className="server-interface-nav-badge">{pendingPaymentsCount}</span>
                )}
              </button>
              
              <button 
                onClick={() => handleMenuClick('ServerSchedule')} 
                className={`server-interface-nav-item ${activeSection === 'ServerSchedule' ? 'server-interface-nav-item-active' : ''}`}
              >
                <FaCalendarAlt className="server-interface-nav-icon" />
                <span className="server-interface-nav-text">Mon Planning</span>
              </button>
              
              <button 
                onClick={() => handleMenuClick('ViewMenuServer')} 
                className={`server-interface-nav-item ${activeSection === 'ViewMenuServer' ? 'server-interface-nav-item-active' : ''}`}
              >
                <FaUtensils className="server-interface-nav-icon" />
                <span className="server-interface-nav-text">Voir Menu</span>
              </button>
            </div>
          </nav>

          {/* Actions header */}
          <div className="server-interface-header-actions">
            <button 
              onClick={() => handleMenuClick('TakeOrder')} 
              className="server-interface-action-btn server-interface-action-btn-primary"
            >
              <FaClipboardList className="server-interface-action-icon" />
              <span className="server-interface-action-text">Prendre Commande</span>
            </button>
            
            <button 
              onClick={handleLogout} 
              className="server-interface-action-btn server-interface-action-btn-secondary"
            >
              <FaSignOutAlt className="server-interface-action-icon" />
              <span className="server-interface-action-text">Déconnexion</span>
            </button>
          </div>
        </div>

        {/* Menu mobile burger */}
        <div className="server-interface-mobile-header">
          <button 
            className={`server-interface-mobile-menu-toggle ${isMenuOpen ? 'server-interface-mobile-menu-toggle-open' : ''}`} 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <div className="server-interface-mobile-menu-line"></div>
            <div className="server-interface-mobile-menu-line"></div>
            <div className="server-interface-mobile-menu-line"></div>
            {pendingPaymentsCount > 0 && (
              <span className="server-interface-mobile-menu-badge">{pendingPaymentsCount}</span>
            )}
          </button>
        </div>
      </header>

      {/* Menu mobile */}
      <div className={`server-interface-mobile-menu ${isMenuOpen ? 'server-interface-mobile-menu-open' : ''}`}>
        <div className="server-interface-mobile-menu-content">
          <div className="server-interface-mobile-menu-header">
            <h3 className="server-interface-mobile-menu-title">Menu</h3>
            <button 
              className="server-interface-mobile-menu-close" 
              onClick={() => setIsMenuOpen(false)}
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="server-interface-mobile-menu-items">
            <button 
              onClick={() => handleMenuClick('home')} 
              className={`server-interface-mobile-menu-item ${activeSection === 'home' ? 'server-interface-mobile-menu-item-active' : ''}`}
            >
              <FaHome className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Accueil</span>
            </button>
            
            <button 
              onClick={() => handleMenuClick('GlobalView')} 
              className={`server-interface-mobile-menu-item ${activeSection === 'GlobalView' ? 'server-interface-mobile-menu-item-active' : ''}`}
            >
              <FaEye className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Vue Globale</span>
            </button>
            
            <button 
              onClick={() => handleMenuClick('MyPaiement')} 
              className={`server-interface-mobile-menu-item ${activeSection === 'MyPaiement' ? 'server-interface-mobile-menu-item-active' : ''}`}
            >
              <FaCreditCard className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Gérer les paiements</span>
              {pendingPaymentsCount > 0 && (
                <span className="server-interface-mobile-menu-badge">{pendingPaymentsCount}</span>
              )}
            </button>
            
            <button 
              onClick={() => handleMenuClick('ServerSchedule')} 
              className={`server-interface-mobile-menu-item ${activeSection === 'ServerSchedule' ? 'server-interface-mobile-menu-item-active' : ''}`}
            >
              <FaCalendarAlt className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Mon Planning</span>
            </button>
            
            <button 
              onClick={() => handleMenuClick('ViewMenuServer')} 
              className={`server-interface-mobile-menu-item ${activeSection === 'ViewMenuServer' ? 'server-interface-mobile-menu-item-active' : ''}`}
            >
              <FaUtensils className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Voir Menu</span>
            </button>
            
            <button 
              onClick={() => handleMenuClick('TakeOrder')} 
              className="server-interface-mobile-menu-item server-interface-mobile-menu-item-primary"
            >
              <FaClipboardList className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Prendre Commande</span>
            </button>
          </div>
          
          <div className="server-interface-mobile-menu-footer">
            <button 
              onClick={handleLogout} 
              className="server-interface-mobile-menu-logout"
            >
              <FaSignOutAlt className="server-interface-mobile-menu-icon" />
              <span className="server-interface-mobile-menu-text">Se déconnecter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Popup de confirmation de déconnexion */}
      {showLogoutConfirm && (
        <div className="server-interface-logout-overlay">
          <div className="server-interface-logout-popup">
            <div className="server-interface-logout-header">
              <div className="server-interface-logout-icon">
                <FaSignOutAlt />
              </div>
              <h3 className="server-interface-logout-title">Confirmation de déconnexion</h3>
            </div>
            <p className="server-interface-logout-message">Êtes-vous sûr de vouloir vous déconnecter ?</p>
            <div className="server-interface-logout-actions">
              <button onClick={handleLogoutCancel} className="server-interface-logout-btn server-interface-logout-btn-cancel">
                <FaCancel />
                Annuler
              </button>
              <button onClick={handleLogoutConfirm} className="server-interface-logout-btn server-interface-logout-btn-confirm">
                <FaCheck />
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <main className="server-interface-main">
        {activeSection === 'GlobalView' && <GlobalView />}
        {activeSection === 'TakeOrder' && <TakeOrder />}
        {activeSection === 'ServerSchedule' && <ServerSchedule />}
        {activeSection === 'MyPaiement' && <MyPaiement />}
        {activeSection === 'home' && <ServerOrders />}
        {activeSection === 'ViewMenuServer' && <ViewMenuServer menuId={menuId}/>}
      </main>
    </div>
  );
};

export default ServerInterface;
